// ============================================================================
// ActionTooltip - Shows requirements for disabled actions
// ============================================================================

import { useEffect, useRef } from 'preact/hooks';
import { tv } from 'tailwind-variants';
import { t } from '../../state/i18n';

// ============================================================================
// Styles
// ============================================================================

const tooltip = tv({
  slots: {
    root: [
      'fixed z-[2100]',
      'bg-rs-stone-dark/95',
      'border border-rs-gold/40',
      'rounded-md',
      'shadow-lg shadow-black/50',
      'min-w-[180px] max-w-[280px]',
      'animate-fade-in',
      'backdrop-blur-sm',
    ],
    header: [
      'px-3 py-1.5',
      'border-b border-rs-stone-light/30',
      'text-rs-dragon text-xs font-semibold font-medieval',
      'tracking-wide',
    ],
    list: 'py-1.5',
    item: ['flex items-start gap-2', 'px-3 py-1', 'text-rs-parchment text-[11px] font-medieval'],
    icon: 'text-rs-dragon text-[10px] mt-0.5 flex-shrink-0',
    closeButton: [
      'absolute top-1 right-1',
      'w-5 h-5',
      'flex items-center justify-center',
      'text-rs-parchment-dark hover:text-rs-parchment',
      'cursor-pointer',
      'transition-colors duration-100',
    ],
  },
  variants: {
    pinned: {
      true: {
        closeButton: 'visible',
        root: 'border-rs-gold/60',
      },
      false: {
        closeButton: 'invisible',
      },
    },
  },
  defaultVariants: {
    pinned: false,
  },
});

// ============================================================================
// Component Props
// ============================================================================

interface ActionTooltipProps {
  readonly reasons: ReadonlyArray<string>;
  readonly isPinned: boolean;
  readonly onClose: () => void;
  readonly parentRect: DOMRect | null;
}

// ============================================================================
// Component
// ============================================================================

export const ActionTooltip = ({ reasons, isPinned, onClose, parentRect }: ActionTooltipProps) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const styles = tooltip({ pinned: isPinned });

  // Handle click outside to close when pinned
  useEffect(() => {
    if (!isPinned) {
      return;
    }

    const handleClickOutside = (e: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    // Delay to prevent immediate close
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }, 10);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isPinned, onClose]);

  // Calculate position using viewport coordinates (since we use position: fixed)
  const getPosition = (): { left: number; top: number } => {
    if (!parentRect) {
      return { left: 0, top: 0 };
    }

    // Position to the right of the menu item using viewport coordinates
    const left = parentRect.right + 8;
    const top = parentRect.top;

    return { left, top };
  };

  // Adjust position to stay in viewport
  useEffect(() => {
    if (!tooltipRef.current || !parentRect) {
      return;
    }

    const rect = tooltipRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // If tooltip goes off right edge, position to left of menu item
    if (rect.right > viewportWidth) {
      tooltipRef.current.style.left = `${parentRect.left - rect.width - 8}px`;
    }

    // If tooltip goes off bottom edge, move up
    if (rect.bottom > viewportHeight) {
      const overflow = rect.bottom - viewportHeight;
      tooltipRef.current.style.top = `${parentRect.top - overflow - 8}px`;
    }
  }, [parentRect, reasons]);

  if (reasons.length === 0) {
    return null;
  }

  const position = getPosition();

  return (
    <div
      ref={tooltipRef}
      class={styles.root()}
      style={{
        left: `${position.left}px`,
        top: `${position.top}px`,
      }}
    >
      {/* Header */}
      <div class={styles.header()}>{t('requirements.requirementsNotMet')}</div>

      {/* Requirements list */}
      <div class={styles.list()}>
        {reasons.map((reason, index) => (
          <div key={index} class={styles.item()}>
            <span class={styles.icon()} aria-hidden="true">
              x
            </span>
            <span>{reason}</span>
          </div>
        ))}
      </div>

      {/* Close button (visible when pinned) */}
      <button
        type="button"
        class={styles.closeButton()}
        onClick={onClose}
        aria-label="Close tooltip"
      >
        x
      </button>

      {/* Decorative corner accents */}
      <div class="absolute -top-[1px] -left-[1px] w-1.5 h-1.5 border-t border-l border-rs-gold/50 rounded-tl-sm" />
      <div class="absolute -top-[1px] -right-[1px] w-1.5 h-1.5 border-t border-r border-rs-gold/50 rounded-tr-sm" />
      <div class="absolute -bottom-[1px] -left-[1px] w-1.5 h-1.5 border-b border-l border-rs-gold/50 rounded-bl-sm" />
      <div class="absolute -bottom-[1px] -right-[1px] w-1.5 h-1.5 border-b border-r border-rs-gold/50 rounded-br-sm" />
    </div>
  );
};
